import {
  AspectRatio,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Input,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { ItemType } from "../../../types/item";
import { FormLabel } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import AdditionalInfoInput from "./AdditionalInfoInput";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { getBaseSku } from "../../../utils/excel";
import { theme } from "../../Theme";
import ImageFullScreenView from "./ImageFullScreenView";
import { ArrowsPointingOutIcon, TrashIcon } from "@heroicons/react/24/outline";
import { PlayIcon } from "@heroicons/react/24/solid";
import { isUrl } from "../../../utils/misc";

type Props = {
  currentItem: ItemType;
  handleDeleteImage: () => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setShowAlert: Dispatch<SetStateAction<boolean>>;
  setAlertMessage: Dispatch<SetStateAction<string>>;
  allSku: string[];
  allTags: string[];
  imageLocations: Array<File | string>;
  setImageLocations: Dispatch<SetStateAction<Array<File | string>>>;
  videoFile: File | string | undefined;
  setVideoFile: Dispatch<SetStateAction<File | string | undefined>>;
};

type BlobUrlRecord = {
  url: string;
  file: File;
};

const ProductDetailsSection = ({
  currentItem,
  allSku,
  allTags,
  imageLocations,
  setImageLocations,
  videoFile,
  setVideoFile,
}: Props) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isCreate = searchParams.get("mode") === "create";
  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = useFormContext();

  // const [imageSize, setImageSize] = useState(100);
  const [showImageFullView, setShowImageFullView] = useState({
    imageSrc: "/union.png",
    show: false,
    type: "image",
  });
  const [blobUrls, setBlobUrls] = useState<BlobUrlRecord[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoFileInputRef = useRef<HTMLInputElement>(null);

  // Generate blob URLs only for File objects, and only once per file
  useEffect(() => {
    const newBlobUrls: BlobUrlRecord[] = [];

    console.log("BLOB URL FILE?", blobUrls);
    imageLocations.forEach((location, index) => {
      if (typeof location !== "string") {
        if (!blobUrls[index] || blobUrls[index].file !== location) {
          if (blobUrls[index]) {
            URL.revokeObjectURL(blobUrls[index].url);
          }

          newBlobUrls[index] = {
            url: URL.createObjectURL(location),
            file: location,
          };
        } else {
          newBlobUrls[index] = blobUrls[index];
        }
      }
    });

    console.log("BLOB URL FILE? 2", newBlobUrls);
    blobUrls.forEach((data, index) => {
      if (!newBlobUrls[index]) {
        URL.revokeObjectURL(data.url);
      }
    });

    setBlobUrls(newBlobUrls);

    // Cleanup function to revoke all blob URLs when component unmounts
    return () => {
      Object.values(blobUrls).forEach((data) => {
        URL.revokeObjectURL(data.url);
      });
    };
  }, [imageLocations]);

  // Get source URL for a location
  const getSourceUrl = (location: File | string, index: number) => {
    if (typeof location === "string") {
      return isUrl(location)
        ? location
        : `${import.meta.env.VITE_API_URL}/objects/${
            currentItem.business
          }/${location}`;
    } else {
      return blobUrls[index]?.url || "";
    }
  };

  const handleImageClick = (index: number) => {
    const location = imageLocations[index];
    const imageUrl = getSourceUrl(location, index);

    setShowImageFullView({
      imageSrc: imageUrl,
      show: true,
      type: "image",
    });
  };

  const handleVideoClick = () => {
    if (!videoFile) {
      return;
    }

    setShowImageFullView({
      imageSrc:
        typeof videoFile === "string"
          ? `${import.meta.env.VITE_API_URL}/objects/${
              currentItem.business
            }/${videoFile}`
          : URL.createObjectURL(videoFile),
      show: true,
      type: "video",
    });
  };

  const handleAddImage = (file: File) => {
    setImageLocations((prev) => [...prev, file]);
  };

  const handleRemoveImage = (index: number) => {
    setImageLocations((prev) => {
      prev.splice(index, 1);

      return [...prev];
    });
  };

  const handleAddVideo = (file: File) => {
    setVideoFile(file);
  };

  const handleRemoveVideo = () => {
    setVideoFile(undefined);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const MAX_SIZE_MB = file.type.startsWith("video/") ? 20 : 5;
    const fileSizeMB = file.size / (1024 * 1024);

    if (fileSizeMB > MAX_SIZE_MB) {
      alert(`File too large (max ${MAX_SIZE_MB}MB)`);
      event.target.value = "";
      return;
    }

    if (file.type.startsWith("image/")) {
      handleAddImage(file);
    } else if (file.type.startsWith("video/")) {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        const duration = video.duration;

        window.URL.revokeObjectURL(video.src);
        video.remove();

        if (duration > 60) {
          alert(`File too long (max 60s)`);
          event.target.value = "";
          return;
        }
      };

      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        video.remove();
      };

      video.src = URL.createObjectURL(file);
      handleAddVideo(file);
    } else {
      console.warn("Unsupported file type:", file.type);
      alert("Please upload an image or video file");
    }
  };

  const handleFileInputClick = (type: string) => {
    if (type === "image" && imageLocations.length === 4) {
      return;
    }

    if (type === "image" && fileInputRef.current) {
      fileInputRef.current.click();
      return;
    }

    if (type === "video" && videoFileInputRef.current) {
      if (!!videoFile) {
        return;
      }

      videoFileInputRef.current.click();
    }
  };

  return (
    <Card
      sx={{
        width: "100%",
        borderRadius: 8,
        border: {
          xs: "none",
          lg: `1px solid ${theme.palette.neutral[300]}`,
        },
        p: {
          xs: 0,
          lg: "32px",
        },
        gap: "24px",
      }}
    >
      <Stack
        direction={"row"}
        sx={{
          px: { xs: "16px", md: 0 },
          gap: { xs: "14px", md: "24px" },
          justifyContent: {
            xs: imageLocations.length > 0 ? "flex-start" : "center",
            md: "center",
          },
          alignItems: "center",
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        {/* uploaded images */}
        {imageLocations.map((location, index) => (
          <MediaThumbnail
            type="image"
            src={getSourceUrl(location, index)}
            index={index}
            isMainDisplay={index === 0}
            isUpload={false}
            onRemove={() => handleRemoveImage(index)}
            onExpand={() => handleImageClick(index)}
            onClick={() => handleImageClick(index)}
          />
        ))}

        {/* upload image button */}
        {imageLocations.length < 4 && (
          <>
            <MediaThumbnail
              type="image"
              onClick={() => handleFileInputClick("image")}
              index={imageLocations.length}
            />

            {/* file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </>
        )}

        {/* upload video button */}
        <>
          <MediaThumbnail
            type="video"
            src={
              !!videoFile
                ? typeof videoFile === "string"
                  ? `${import.meta.env.VITE_API_URL}/objects/${
                      currentItem.business
                    }/${videoFile}`
                  : URL.createObjectURL(videoFile)
                : undefined
            }
            onRemove={() => handleRemoveVideo()}
            onExpand={() => handleVideoClick()}
            onClick={() =>
              !!videoFile ? handleVideoClick() : handleFileInputClick("video")
            }
            isUpload={!videoFile}
          />

          {/* file input */}
          <input
            ref={videoFileInputRef}
            type="file"
            accept="video/mp4"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </>
      </Stack>

      {videoFile && imageLocations.length === 0 && (
        <Typography
          level="text-sm-regular"
          sx={{
            width: "100%",
            textAlign: "center",
            color: theme.palette.danger[600],
          }}
        >
          {t("catalog.inventory.editForm.error.pleaseAdd1Photo")}
        </Typography>
      )}

      {currentItem && (
        <Grid container spacing={2}>
          <Grid xs={12} lg={6}>
            <FormControl sx={{ flex: "1" }} error={!!errors.sku}>
              <FormLabel>{t("catalog.inventory.editForm.sku")}</FormLabel>
              <Input
                type="text"
                placeholder={t("catalog.inventory.editForm.sku")}
                defaultValue={currentItem?.sku}
                aria-label="sku"
                {...register("sku", {
                  required: t("commons.error.form.required", {
                    label: t("catalog.inventory.editForm.sku"),
                  }),
                  validate: (value) => {
                    const duplicate = allSku.filter((i) =>
                      isCreate
                        ? getBaseSku(i) === getBaseSku(value)
                        : i === value
                    );
                    // console.log("BANANA VALIDATE", duplicate);
                    return duplicate.length > 1
                      ? t("commons.error.form.duplicateSKUs")
                      : true;
                  },
                })}
                error={!!errors.sku}
                className="min-h-[3.5rem]"
              />
              <FormHelperText>{(errors.sku as any)?.message}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid xs={12} lg={6}>
            <FormControl sx={{ flex: "1" }} error={!!errors.productName}>
              <FormLabel>
                {t("catalog.inventory.editForm.productName")}
              </FormLabel>
              <Input
                type="text"
                placeholder={t("catalog.inventory.editForm.productName")}
                defaultValue={currentItem?.productName}
                aria-label="productName"
                {...register("productName", {
                  required: t("commons.error.form.required", {
                    label: t("catalog.inventory.editForm.productName"),
                  }),
                })}
                error={!!errors.productName}
                className="min-h-[3.5rem]"
              />
              <FormHelperText>
                {(errors.productName as any)?.message}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid xs={12} lg={6}>
            <FormControl sx={{ flex: "1" }} error={!!errors.inventoryType}>
              <FormLabel>
                {t("catalog.inventory.editForm.inventoryType")}
              </FormLabel>
              <Input
                type="text"
                placeholder={t("catalog.inventory.editForm.inventoryType")}
                defaultValue={currentItem?.inventoryType}
                aria-label="inventoryType"
                {...register("inventoryType", {
                  required: t("commons.error.form.required", {
                    label: t("catalog.inventory.editForm.inventoryType"),
                  }),
                })}
                error={!!errors.inventoryType}
                className="min-h-[3.5rem]"
              />
              <FormHelperText>
                {(errors.inventoryType as any)?.message}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid xs={12} lg={6}>
            <Controller
              name={"tags.tags"}
              control={control}
              render={({ field }) => {
                const { onChange } = field;
                return (
                  <FormControl sx={{ flex: "1" }}>
                    <FormLabel>
                      {t("catalog.inventory.editForm.subCategory")}
                      {" (Optional)"}
                    </FormLabel>
                    <Autocomplete
                      options={allTags}
                      placeholder={t("catalog.inventory.editForm.subCategory")}
                      getOptionLabel={(option) => option}
                      freeSolo
                      multiple
                      limitTags={3}
                      defaultValue={currentItem.tags?.tags ?? []}
                      onChange={(_e, data) => onChange(data)}
                    />
                  </FormControl>
                );
              }}
            />
          </Grid>
          <Grid xs={12}>
            <Controller
              name="productDescription"
              control={control}
              defaultValue={currentItem?.productDescription}
              render={({ field: { value, ref } }) => (
                <FormControl sx={{ flex: "1" }}>
                  <FormLabel>
                    {t("catalog.inventory.editForm.productDescription")}
                  </FormLabel>
                  <Textarea
                    ref={ref}
                    placeholder={t(
                      "catalog.inventory.editForm.productDescription"
                    )}
                    minRows={4}
                    onChange={(
                      event: React.ChangeEvent<HTMLTextAreaElement>
                      // newValue: string | null
                    ) => {
                      setValue("productDescription", event.target.value ?? "");
                    }}
                    defaultValue={value}
                  />
                </FormControl>
              )}
            />
          </Grid>

          <Grid xs={12}>
            <AdditionalInfoInput />
          </Grid>
        </Grid>
      )}

      <ImageFullScreenView
        imageSrc={showImageFullView.imageSrc}
        show={showImageFullView.show}
        type={showImageFullView.type}
        onClose={() => {
          setShowImageFullView({
            imageSrc: "/union.png",
            show: false,
            type: "image",
          });
        }}
      />
    </Card>
  );
};

const MediaThumbnail = ({
  type,
  src,
  index,
  isMainDisplay = false,
  onClick,
  onRemove,
  onExpand,
  isUpload = true,
}: {
  type: string;
  src?: string;
  index?: number;
  isMainDisplay?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  onExpand?: () => void;
  onAdd?: () => void;
  isUpload?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="plain"
      onClick={onClick}
      sx={{
        p: 0,
        position: "relative",
        ...(!isUpload && {
          "&:hover .show-on-hover": { opacity: 1 },
          "&:hover .hover-overlay": { opacity: 0.5 },
        }),
      }}
    >
      <AspectRatio
        ratio="1"
        variant="plain"
        sx={{
          width: { xs: "100px", md: "180px" },
          height: { xs: "100px", md: "180px" },
        }}
      >
        {type === "image" && src && (
          <img
            alt={`media-${index}`}
            height={"100%"}
            src={src}
            width={"100%"}
          />
        )}
        {type === "video" && src && (
          <video
            src={src}
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              top: 0,
              left: 0,
            }}
          />
        )}
        {isUpload && (
          <img
            alt="upload-placeholder"
            height={"100%"}
            src={
              type === "video"
                ? "/inventory-video-upload-placeholder.png"
                : "/inventory-image-upload-placeholder.png"
            }
            width={"100%"}
          />
        )}
      </AspectRatio>

      {/* Main Display Chip */}
      {isMainDisplay && (
        <Chip
          sx={{
            position: "absolute",
            top: { xs: "4px", md: "8px" },
            left: { xs: "4px", md: "8px" },
            borderRadius: "8px",
            borderColor: theme.palette.others.active_chip_border,
            paddingY: "4px",
            paddingX: { xs: "5.33px", md: "8px" },
            backgroundColor: theme.palette.others.active_chip_bg,
            color: theme.palette.others.active_chip_border,
            zIndex: 40,
          }}
        >
          <Typography level="text-sm-semibold">Main Display</Typography>
        </Chip>
      )}

      {/* Upload Label */}
      {isUpload && (
        <Typography
          level="text-sm-semibold"
          sx={{
            position: "absolute",
            top: { xs: "71.41px", md: "133.05px" },
            color: theme.palette.primary[500],
          }}
        >
          {type === "video"
            ? t("catalog.inventory.editForm.videoLabel")
            : `${t("catalog.inventory.editForm.imageLabel")} ${
                (index || 0) + 1
              }`}
        </Typography>
      )}

      {/* Video Play Button */}
      {type === "video" && src && (
        <Button
          variant="plain"
          onClick={(e) => {
            e.stopPropagation();
            onExpand?.();
          }}
          sx={{
            position: "absolute",
            borderRadius: "100%",
            width: "56.7px",
            minWidth: "56.7px",
            height: "56.7px",
            backgroundColor: theme.palette.others.video_playbutton_bg,
            opacity: "72%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <PlayIcon
            width={28.8}
            height={28.8}
            style={{
              opacity: 100,
              color: theme.palette.common.white,
            }}
          />
        </Button>
      )}

      {/* Overlay */}
      {!isUpload && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            backgroundColor: theme.palette.neutral[500],
            opacity: 0,
            transition: "opacity 0.2s ease-in-out",
            zIndex: 30,
            width: { xs: "100px", md: "180px" },
            height: { xs: "100px", md: "180px" },
          }}
          className="hover-overlay"
        />
      )}

      {/* Action Buttons */}
      {!isUpload && (
        <Stack
          direction="row"
          sx={{
            position: "absolute",
            gap: "8px",
            zIndex: 40,
            bottom: "8px",
            right: { xs: "4px", md: "8px" },
          }}
        >
          <IconButton
            sx={{
              opacity: 0,
              transition: "opacity 0.2s ease-in-out",
              backgroundColor: theme.palette.common.white,
              p: "8px",
            }}
            className="show-on-hover"
            onClick={(e) => {
              e.stopPropagation();
              onExpand?.();
            }}
          >
            <ArrowsPointingOutIcon width="16px" height="16px" />
          </IconButton>

          <IconButton
            sx={{
              opacity: 0,
              transition: "opacity 0.2s ease-in-out",
              backgroundColor: theme.palette.common.white,
              p: "8px",
            }}
            className="show-on-hover"
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
          >
            <TrashIcon width="16px" height="16px" />
          </IconButton>
        </Stack>
      )}
    </Button>
  );
};

export default ProductDetailsSection;
